<script lang="ts">
import { computed, ref } from 'vue';

import cursorZoomIn from '@sg/resources/assets/icons/cursors/zoom-in.svg';
import cursorZoomOut from '@sg/resources/assets/icons/cursors/zoom-out.svg';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';
import { PhxsgProductAnimation, type ProductAnimationI18n } from '@sg/src/View/Components/Molecules/ProductAnimation';
import { PhxsgVideoPlayer } from '@sg/src/View/Components/Molecules/VideoPlayer';
import { PhxsgBackgroundVideo } from '@sg/src/View/Components/Organisms/BackgroundVideo';
import {
  PhxsgLightbox as PhxsgCoreLightbox,
  type PhxsgLightboxProps as CoreProps,
  type LightboxItem,
  type LightboxI18n,
  type LightboxComponents,
  type LightboxCustomData,
  type LightboxCustomItem,
  type LightboxPictureItem,
  type LightboxBackgroundVideoItem,
  type LightboxVideoItem,
} from '@sg-core/src/View/Components/Organisms/Lightbox';

export interface LightboxProductAnimationData extends LightboxCustomData {
  sources: string[];
  mode?: 'auto' | 'manual';
  backgroundColor?: string | null;
  sourceCount?: number;
  i18n?: ProductAnimationI18n;
}

export interface LightboxProductAnimationItem extends LightboxCustomItem {
  type: 'product-animation';
  data: LightboxProductAnimationData;
}

export type PiaLightboxItem =
  LightboxPictureItem | LightboxBackgroundVideoItem | LightboxVideoItem | LightboxProductAnimationItem;

export type { LightboxItem, LightboxI18n, LightboxPictureItem, LightboxBackgroundVideoItem, LightboxVideoItem };

export interface PhxsgLightboxProps extends Omit<CoreProps, 'components' | 'items'> {
  items: PiaLightboxItem[];
  components?: LightboxComponents;
}

interface LightboxExposed {
  open: (index?: number) => void;
  close: () => void;
  goTo: (index: number) => void;
  prev: () => void;
  next: () => void;
  currentIndex: number;
  isOpen: boolean;
  isZoomed: boolean;
  resetZoom: () => void;
}

function isProductAnimationItem(item: LightboxItem): item is LightboxProductAnimationItem {
  return item.type === 'product-animation';
}
</script>

<script setup lang="ts">
defineOptions({ inheritAttrs: false });

const {
  id,
  items,
  loop = false,
  teleportTarget = null,
  i18n = {},
  components: componentOverrides = {},
  cursor: cursorOverrides = {},
  steps = 3,
  counter = false,
  bullets = false,
  modelValue = null,
  zoomImageOnly = true,
} = defineProps<PhxsgLightboxProps>();

defineEmits<{
  'update:modelValue': [index: number];
  change: [index: number];
  open: [index: number];
  close: [];
}>();

const coreLightboxRef = ref<LightboxExposed | null>(null);

const closeIcon = { start: { id: 'close', size: 'sm' } } as const;
const prevIcon = { start: { id: 'phxsg-arrow-left', size: 'md' } } as const;
const nextIcon = { start: { id: 'phxsg-arrow-right', size: 'md' } } as const;
const zoomOutIcon = { start: { id: 'minus', size: 'xs' } } as const;
const zoomInIcon = { start: { id: 'plus', size: 'xs' } } as const;

/** Merged component overrides: PIA defaults + any explicit overrides. */
const mergedComponents = computed<LightboxComponents>(() => ({
  backgroundVideoComponent: PhxsgBackgroundVideo,
  videoPlayerComponent: PhxsgVideoPlayer,
  ...componentOverrides,
}));

const mergedCursor = computed(() => ({
  in: cursorZoomIn,
  out: cursorZoomOut,
  ...cursorOverrides,
}));

defineExpose({
  open(index = 0) {
    coreLightboxRef.value?.open(index);
  },
  close() {
    coreLightboxRef.value?.close();
  },
  goTo(index: number) {
    coreLightboxRef.value?.goTo(index);
  },
  prev() {
    coreLightboxRef.value?.prev();
  },
  next() {
    coreLightboxRef.value?.next();
  },
  get currentIndex() {
    return coreLightboxRef.value?.currentIndex ?? 0;
  },
  get isOpen() {
    return coreLightboxRef.value?.isOpen ?? false;
  },
  get isZoomed() {
    return coreLightboxRef.value?.isZoomed ?? false;
  },
  resetZoom() {
    coreLightboxRef.value?.resetZoom();
  },
});
</script>

<template>
  <PhxsgCoreLightbox
    :id="id"
    ref="coreLightboxRef"
    :items="items"
    :loop="loop"
    :teleport-target="teleportTarget"
    :i18n="i18n"
    :components="mergedComponents"
    :cursor="mergedCursor"
    :steps="steps"
    :counter="counter"
    :bullets="bullets"
    :model-value="modelValue"
    :zoom-image-only="zoomImageOnly"
    v-bind="$attrs"
    @update:model-value="$emit('update:modelValue', $event)"
    @change="$emit('change', $event)"
    @open="$emit('open', $event)"
    @close="$emit('close')"
  >
    <!-- PIA Close button -->
    <template #closeButton="{ onClick }">
      <PhxsgButton
        icon-only
        variant="ghost"
        :icon="closeIcon"
        class="phxsg-lightbox__close phxsg-lightbox__control"
        :aria-label="i18n?.close?.label ?? 'Close'"
        @click="onClick"
      >
        {{ i18n?.close?.label ?? 'Close' }}
      </PhxsgButton>
    </template>

    <!-- PIA Previous button -->
    <template #navPrev="{ onClick, disabled }">
      <PhxsgButton
        icon-only
        variant="ghost"
        :icon="prevIcon"
        class="phxsg-lightbox__nav phxsg-lightbox__nav--prev phxsg-lightbox__control"
        :disabled="disabled"
        :aria-label="i18n?.navPrev?.label ?? 'Previous'"
        @click="onClick"
      >
        {{ i18n?.navPrev?.label ?? 'Previous' }}
      </PhxsgButton>
    </template>

    <!-- PIA Next button -->
    <template #navNext="{ onClick, disabled }">
      <PhxsgButton
        icon-only
        variant="ghost"
        :icon="nextIcon"
        class="phxsg-lightbox__nav phxsg-lightbox__nav--next phxsg-lightbox__control"
        :disabled="disabled"
        :aria-label="i18n?.navNext?.label ?? 'Next'"
        @click="onClick"
      >
        {{ i18n?.navNext?.label ?? 'Next' }}
      </PhxsgButton>
    </template>

    <!-- PIA Zoom controls -->
    <template #zoomControl="{ zoomIn, zoomOut, scalePercent, canZoomIn, canZoomOut }">
      <div class="phxsg-lightbox__zoom-controls phxsg-lightbox__zoom-exclude">
        <PhxsgButton
          icon-only
          variant="ghost"
          :icon="zoomOutIcon"
          class="phxsg-lightbox__zoom-btn phxsg-lightbox__control"
          :disabled="!canZoomOut"
          :aria-label="i18n?.zoom?.out ?? 'Zoom out'"
          @click="zoomOut"
        >
          {{ i18n?.zoom?.out ?? 'Zoom out' }}
        </PhxsgButton>
        <span class="phxsg-lightbox__zoom-level">{{ scalePercent }}%</span>
        <PhxsgButton
          icon-only
          variant="ghost"
          :icon="zoomInIcon"
          class="phxsg-lightbox__zoom-btn phxsg-lightbox__control"
          :disabled="!canZoomIn"
          :aria-label="i18n?.zoom?.in ?? 'Zoom in'"
          @click="zoomIn"
        >
          {{ i18n?.zoom?.in ?? 'Zoom in' }}
        </PhxsgButton>
      </div>
    </template>

    <template #customItem="{ item }">
      <PhxsgProductAnimation
        v-if="isProductAnimationItem(item)"
        :sources="item.data.sources"
        mode="auto"
        :background-color="item.data.backgroundColor"
        :source-count="item.data.sourceCount"
        :i18n="item.data.i18n"
        fit="contain"
        class="phxsg-lightbox__product-animation"
      />
    </template>
  </PhxsgCoreLightbox>
</template>

<style src="./lightbox.css" />
